// HOME
import { useNavigate, Link, useOutletContext } from 'react-router-dom';
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import Carousel from "react-bootstrap/Carousel";
// funciones
import { slugify } from "../funcionesDatos/Textos";
  
// Card reutilizable para Home, Búsqueda y otras secciones.
// Props:
// - servicio: objeto Servicio (con nombre, descripcion, categoria, ciudad, imagenes, paquetes, resumenReseñas)
// - onVerServicio: function(servicio) => void
// - mostrarCiudad: boolean (default true)
// - clampDesc: number de líneas (default 4)
// - className: clases extra para el wrapper
// - textoBoton: texto del botón (default "Ver servicio")

function formatearMonedaSimple(valor, divisa = "COP") {
  try {
    const currency = (divisa || "COP").toString().trim().toUpperCase();
    const maxDigits = (currency === "USD" || currency === "EUR" || currency === "PEN") ? 2 : 0;

    return new Intl.NumberFormat("es-CO", {
      style: "currency",
      currency,
      maximumFractionDigits: maxDigits,
    }).format(Number(valor));
  } catch {
    return `${valor}`;
  }
}

// El backend ya calcula el precio final (con comisión de Zervicy + fee de pasarela incluidos) y lo manda
// como `totalClienteDesde` en cada paquete — ver `enriquecerPaquetesConTotalCliente` en
// backend/funciones/variables.js, aplicado en /obtener-servicios-mas-contratados, /buscar-servicios y
// /obtener-favoritos. Antes esto se recalculaba aquí con una copia manual de comisionHPM/feeWompi, y se
// desincronizaba cada vez que cambiábamos esos valores en el backend (pasó el 2026-07-24). Si por algún
// motivo el campo no viene (respuesta vieja/caché), mostramos el precio crudo como último recurso.
function obtenerPrecioDesde(servicio) {
  const paquete = servicio?.paquetes?.[0];
  const d = paquete?.divisa || "COP";
  const totalCliente = paquete?.totalClienteDesde;
  const p = paquete?.precio;

  if (!Number.isFinite(totalCliente) && !Number.isFinite(p)) return null;

  return { precio: Number.isFinite(totalCliente) ? totalCliente : p, divisa: d };
}

function obtenerImagen(servicio) {
  const arr = servicio?.imagenes;
  if (!Array.isArray(arr)) return null;
  return arr.find(Boolean) || null;
}

// desdeDonde: "home" | "busqueda" | "reserva" | "pagos"
function CardServicio({ servicio, mostrarCiudad = true, clampDesc = 4, className = "", textoBoton = "Ver servicio", desdeDonde }) {
  //console.log("servicio: ", servicio);
  const navigate = useNavigate();
  const zona = (desdeDonde).toString().trim().toLowerCase();
  const esHome     = zona === "home";
  const esBusqueda = zona === "busqueda";
  const esReserva  = zona === "reserva";
  const esPagos    = zona === "pagos";
  const esLista    = esHome || esBusqueda; // home y busqueda comparten layout

  const s = servicio || {};

  const titulo      = (s?.nombre && s.nombre !== "NA") ? String(s.nombre) : "Servicio";
  const categoria   = (s?.categoria && s.categoria !== "NA") ? String(s.categoria) : "";
  const ciudad      = (s?.ciudad && s.ciudad !== "NA") ? String(s.ciudad) : "";
  const descripcion = (s?.descripcion && s.descripcion !== "NA") ? String(s.descripcion) : "";
  const imagen      = obtenerImagen(s);
  const precioDesde = obtenerPrecioDesde(s);

  const resumen      = s?.resumenReseñas || null;
  const promedio     = Number(resumen?.promedio);
  const totalRes     = Number(resumen?.total);
  const tieneReseñas = Number.isFinite(promedio) && Number.isFinite(totalRes) && totalRes > 0;

  const usernameProveedor = (s?.usuario && s.usuario !== "NA") ? String(s.usuario) : null;
  const avatarProveedor   = (s?.avatarProveedor && s.avatarProveedor !== "NA") ? s.avatarProveedor : null;
  const esUrgente          = Boolean(s?.urgente);

  // Mismos colores/íconos que ya usamos en el mapa "cerca de mí" (mapaResultadosBusqueda.jsx) para
  // que sea el mismo lenguaje visual en toda la app: verde = Local, morado = Digital, naranja =
  // Domicilio. "Ambos" existe en el enum del modelo pero nunca se permite crear, no se contempla.
  const badgeTipo = (() => {
    if (s?.tipoServicio === "Digital") return { texto: "Digital", color: "var(--violeta-mapa)", icono: "bi-laptop" };
    if (s?.tipoDesplazamiento === "Domicilio") return { texto: "A domicilio", color: "var(--naranja-mapa)", icono: "bi-truck" };
    if (s?.tipoDesplazamiento === "EnLocal") return { texto: "Local", color: "var(--verde-mapa)", icono: "bi-geo-alt-fill" };
    return null;
  })();


  async function verServicio() {
    const id     = servicio?.idServicio;
    const nombre = (servicio?.nombre && servicio.nombre !== "NA") ? servicio.nombre : "servicio";
    const slug   = await slugify(nombre) || "servicio";
    return navigate("/servicio/" + slug + "-" + id);
  }

  // Toda la card navega al servicio (no solo el botón) en las vistas de lista — el botón se queda
  // como affordance visual, pero detiene la propagación para no disparar el navigate del padre 2 veces.
  function manejarClickCard() {
    if (esLista) verServicio();
  }



  return (
    <div
      className={`card h-100 shadow-sm border-0 ${className}`}
      style={{ overflow: "hidden", cursor: esLista ? "pointer" : undefined }}
      onClick={manejarClickCard}
    >
      {/* Imagen */}
      <div style={{ position: "relative" }}>
        {imagen ? (
          <img
            src={imagen}
            alt={titulo}
            className="w-100"
            style={ esReserva
              ? { height: 140, objectFit: "cover" }  // reserva
              : (esPagos
                ? { height: 220, objectFit: "cover" }  // pagos
                : { width: "100%", aspectRatio: "1 / 1", objectFit: "cover" }) // busqueda y home: cuadrada, no horizontal
            }
            loading="lazy"
          />
        ) : (
          <div
            style={esReserva
              ? { width: '100%', aspectRatio: '1 / 1' } // reserva
              : (esPagos
                ? { height: 220 }  // pagos
                : { width: "100%", aspectRatio: "1 / 1" }) // busqueda y home
            }
            className="bg-light"
          />
        )}

        {/* Urgente: ribbon sobre la imagen, a propósito llamativo (a diferencia de "Nuevo", que es
            solo informativo) — es un diferenciador de negocio que debe resaltar para impulsar reservas. */}
        {esUrgente ? (
          <span
            className="badge d-flex align-items-center gap-1"
            style={{
              position: "absolute",
              top: 10,
              left: 10,
              background: "var(--rojo-error)",
              color: "var(--blanco)",
              fontSize: 12,
              padding: "5px 9px",
              boxShadow: "0 2px 6px rgba(var(--negro-rgb), 0.25)",
            }}
          >
            <i className="bi bi-lightning-charge-fill"></i> Urgente
          </span>
        ) : null}

        {/* Local / Digital / A domicilio — esquina opuesta a Urgente, mismo lenguaje de color del mapa. */}
        {badgeTipo ? (
          <span
            className="badge d-flex align-items-center gap-1"
            style={{
              position: "absolute",
              top: 10,
              right: 10,
              background: badgeTipo.color,
              color: "var(--blanco)",
              fontSize: 12,
              padding: "5px 9px",
              boxShadow: "0 2px 6px rgba(var(--negro-rgb), 0.25)",
            }}
          >
            <i className={`bi ${badgeTipo.icono}`}></i> {badgeTipo.texto}
          </span>
        ) : null}
      </div>

      <div className="card-body d-flex flex-column">
        {/* Título + badges */}
        <div className="d-flex justify-content-between align-items-start gap-2">
          <div className="fw-semibold" style={{ lineHeight: 1.2, flex: 1, minWidth: 0 }}>
            <div
              style={{
                display: "-webkit-box",
                WebkitLineClamp: 3,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
                minHeight: "2.4em", // reserva 2 líneas aunque el título sea corto, para parejar cards en una fila
              }}
            >
              {titulo}
            </div>
          </div>

          <div className="text-end" style={{ flexShrink: 0 }}>
            {categoria ? <span className="badge text-bg-light">{categoria}</span> : null}
            {(mostrarCiudad && ciudad) ? (
              <div className="mt-1">
                <span className="badge text-bg-primary">{ciudad}</span>
              </div>
            ) : null}
          </div>
        </div>

        {/* Descripción */}
        {descripcion ? (
          <div
            className="mt-2 text-body-secondary"
            style={{
              fontSize: 13,
              display: "-webkit-box",
              WebkitLineClamp: clampDesc,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
              minHeight: `${clampDesc * 1.4}em`, // reserva el espacio de las clampDesc líneas aunque el texto sea corto
            }}
          >
            {descripcion}
          </div>
        ) : null}

        {/* Rating: si no tiene reseñas todavía no mostramos nada (no le mostramos al comprador
            que el servicio no tiene reseñas) — pero dejamos un placeholder invisible con la
            misma altura para que el ritmo vertical siga parejo entre cards. */}
        <div className="mt-2 d-flex align-items-center gap-2" style={{ fontSize: 13 }}>
          {tieneReseñas ? (
            <>
              <span className="fw-semibold">⭐ {promedio.toFixed(2)}</span>
              <span className="text-body-secondary">({totalRes})</span>
            </>
          ) : (
            <span className="badge text-bg-light d-flex align-items-center gap-1" style={{ width: "fit-content", visibility: "hidden" }} aria-hidden="true">
              <i className="bi bi-stars"></i> Nuevo
            </span>
          )}
        </div>

        {/* Precio + botón */}
        <div className="mt-auto pt-3">
          {/* Proveedor */}
          {usernameProveedor ? (
            <Link
              to={`/${usernameProveedor}`}
              onClick={(e) => e.stopPropagation()}
              className="d-flex align-items-center gap-2 mb-3 text-decoration-none"
              style={{ borderTop: "1px solid var(--gris-modal-texto)", paddingTop: 10 }}
            >
              {avatarProveedor ? (
                <img
                  src={avatarProveedor}
                  alt={usernameProveedor}
                  style={{ width: 28, height: 28, borderRadius: "50%", objectFit: "cover", flexShrink: 0, border: "1.5px solid var(--color-border)" }}
                />
              ) : (
                <div style={{ width: 28, height: 28, borderRadius: "50%", background: "var(--color-border)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 700, color: "var(--gris-suave)", flexShrink: 0 }}>
                  {usernameProveedor.charAt(0).toUpperCase()}
                </div>
              )}
              <span className="text-truncate" style={{ fontSize: 12, color: "var(--gris-suave)", fontWeight: 500 }}>@{usernameProveedor}</span>
            </Link>
          ) : null}

          {esLista && precioDesde ?  // home y busqueda
          <div className="mb-2 fw-semibold" style={{ fontSize: 14 }}>
            Desde {formatearMonedaSimple(precioDesde.precio, precioDesde.divisa)}
          </div>
          : null}

          {esLista ? // home y busqueda
            <button type="button" className="btn btn-primary w-100" onClick={(e) => { e.stopPropagation(); verServicio(); }} >
              {textoBoton}
            </button>
          : null // reserva y pagoos
          }
        </div>
      </div>
    </div>
  );
}

export default CardServicio;